import {ApiError, clearCsrfToken} from '../api/http';
import {
  authApi,
  type LoginPayload,
  type RegisterPayload,
} from '../api/authApi';
import type {User} from '../types/models';

type AuthListener = () => void;

class AuthStore {
  user: User | null = null;
  loading = true;

  private listeners = new Set<AuthListener>();

  subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    this.listeners.forEach(listener => listener());
  }

  async bootstrap(): Promise<void> {
    this.loading = true;
    this.emit();

    try {
      this.user = await authApi.me();
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('Authentication bootstrap failed:', error);
      }
      this.user = null;
    } finally {
      this.loading = false;
      this.emit();
    }
  }

  async login(payload: LoginPayload): Promise<User> {
    await authApi.csrf();
    const response = await authApi.login(payload);
    this.user = response.user;
    this.loading = false;
    this.emit();
    return response.user;
  }

  async register(payload: RegisterPayload): Promise<User> {
    await authApi.csrf();
    const response = await authApi.register(payload);
    this.user = response.user;
    this.loading = false;
    this.emit();
    return response.user;
  }

  async logout(): Promise<void> {
    try {
      await authApi.csrf();
      await authApi.logout();
    } finally {
      this.user = null;
      clearCsrfToken();
      this.emit();
    }
  }
}

export const authStore = new AuthStore();
